import Dialog from "ui/elements/dialog/Dialog";
import {i18n} from "ui/i18n";
import Form from "ui/elements/form/Form";
import {SCENE_ANGLE} from "ui/config";

/**
 * @typedef {object} T_MovementSetup
 * @property {number} angle
 * @property {number} distance
 * @property {boolean} isInteger
 */

export default class MovementSetup extends Dialog {
	constructor() {
		super({
			title: i18n('title.movement-picker'),
			buttons: [
				{
					caption: i18n('buttons.apply'),
					role: 'apply',
					icon: '/images/icons.svg#icon-svg-check'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(res: T_MovementSetup|null)}
		 * @private
		 */
		this._onComplete = null;

		void this._buildForm();
	}

	/**
	 * @returns {Promise<T_MovementSetup|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);
		if (role === 'apply') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data);
			}
		}
	}

	/**
	 * @private
	 * @param {Object} data
	 * @returns {void}
	 */
	_apply(data) {
		if (this._onComplete) {
			let angle = parseInt(data.angle);

			if (data.direction !== 'custom') {
				angle = ({
					'left': 180,
					'right': 0,
					'top': 90,
					'bottom': 270,
					'top-left': 180 - SCENE_ANGLE,
					'top-right': SCENE_ANGLE,
					'bottom-right': 360 - SCENE_ANGLE,
					'bottom-left': 180 + SCENE_ANGLE,
				})[data.direction];
			}

			this._onComplete({
				angle,
				distance: parseInt(data.distance),
				isInteger: !!data.isInteger,
			});
			this._onComplete = null;
			this.destroy();
		}
	}

	/**
	 * @private
	 * @returns {Promise<void>}
	 */
	async _buildForm() {
		let el = document.createElement('div');
		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: {angle: 0, distance: 100, isInteger: true, direction: 'top-left'},
			fields: [
				{
					type: 'radio',
					name: 'direction',
					options: [
						{value: 'top', text: i18n('inputs.layer.directions.top')},
						{value: 'top-left', text: i18n('inputs.layer.directions.top-left')},
						{value: 'left', text: i18n('inputs.layer.directions.left')},
						{value: 'bottom-left', text: i18n('inputs.layer.directions.bottom-left')},
						{value: 'bottom', text: i18n('inputs.layer.directions.bottom')},
						{value: 'bottom-right', text: i18n('inputs.layer.directions.bottom-right')},
						{value: 'right', text: i18n('inputs.layer.directions.right')},
						{value: 'top-right', text: i18n('inputs.layer.directions.top-right')},
						{value: 'custom', text: i18n('inputs.layer.directions.custom')},
					],
					caption: i18n('inputs.layer.direction'),
				},
				{
					type: 'uint',
					name: 'angle',
					min: 0,
					max: 360,
					step: 1,
					caption: i18n('inputs.layer.angle'),
					disableIf: (values) => {
						return values.direction !== 'custom';
					}
				},
				{
					type: 'uint',
					name: 'distance',
					min: 0,
					max: 2000,
					step: 1,
					caption: i18n('inputs.layer.distance'),
				},
				{
					type: 'checkbox',
					name: 'isInteger',
					caption: i18n('inputs.layer.round'),
				},
			]
		});
		this.setContent(el);
	}

	/**
	 * @returns {void}
	 */
	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(null);
			this._onComplete = null;
		}
		super.destroy();
	}
}
